import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

export default function Confidentialite() {
  const { language } = useLanguage();
  return (
    <div className="min-h-screen pt-20 lg:pt-24" data-testid="legal-page">
      <SEO
        title="Politique de confidentialité (RGPD) - AfriOne BD"
        description="Comment AfriOne BD collecte, utilise et protège vos données personnelles, conformément au RGPD."
        canonical="https://afrionebd.eu/confidentialite"
      />
      <section className="py-12 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">Protection de vos données</span>
            <h1 className="text-3xl lg:text-4xl font-bold font-heading text-text_primary mt-2 mb-2 tracking-tight">
              Politique de confidentialité
            </h1>

            {/* Document juridique : il fait foi en français. Une traduction
                de complaisance créerait une ambiguïté sur le texte applicable. */}
            {language === 'en' && (
              <p className="mb-6 p-4 border border-border bg-secondary/20 text-sm text-text_secondary">
                This privacy policy is available in French only. The French version is the
                legally binding text. For any question, write to{' '}
                <a href="mailto:contact@afrionebd.eu" className="text-primary hover:underline">contact@afrionebd.eu</a>.
              </p>
            )}
            <p className="text-sm text-text_secondary mb-10">Dernière mise à jour : octobre 2026</p>

            <div className="space-y-8 text-text_secondary leading-relaxed">
              <div>
                <p>
                  AfriOne BD accorde une grande importance à la protection de vos données personnelles.
                  La présente politique explique quelles données nous collectons, pourquoi, et quels sont
                  vos droits, conformément au Règlement Général sur la Protection des Données (RGPD).
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">1. Responsable du traitement</h2>
                <p>
                  <strong>AfriOne BD</strong> - Rue Saint-Nicolas 227, 4000 Liège, Belgique (BCE 1031.014.285).
                  Contact : <a href="mailto:contact@afrionebd.eu" className="text-primary hover:underline">contact@afrionebd.eu</a>.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">2. Données collectées</h2>
                <p>Nous collectons uniquement les données nécessaires au traitement de vos commandes et demandes :</p>
                <ul className="mt-3 space-y-1">
                  <li>• Nom et prénom</li>
                  <li>• Adresse email</li>
                  <li>• Numéro de téléphone</li>
                  <li>• Adresse de livraison</li>
                  <li>• Détails de vos commandes</li>
                  <li>• Le contenu des messages envoyés via le formulaire de contact</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">3. Finalités et base légale</h2>
                <ul className="mt-1 space-y-1">
                  <li>• <strong>Traiter et livrer vos commandes</strong> (exécution du contrat)</li>
                  <li>• <strong>Vous contacter</strong> au sujet de votre commande ou de votre demande (exécution du contrat)</li>
                  <li>• <strong>Respecter nos obligations légales</strong> (comptabilité, facturation)</li>
                </ul>
                <p className="mt-3">
                  Nous n'utilisons pas vos données à des fins publicitaires et ne pratiquons pas de profilage.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">4. Destinataires des données</h2>
                <p>
                  Vos données ne sont <strong>ni vendues, ni louées, ni cédées</strong> à des fins
                  commerciales. Elles sont accessibles à AfriOne BD et, le cas échéant, aux personnes
                  assurant la livraison de votre commande. Pour les paiements en ligne, les données
                  nécessaires à la transaction sont traitées par notre prestataire de paiement
                  <strong> Mollie B.V.</strong> (Pays-Bas), établi dans l'Union européenne : les
                  coordonnées de votre carte bancaire sont saisies directement chez lui et ne transitent
                  jamais par nos serveurs. Nos données sont hébergées sur un serveur sécurisé situé dans
                  l'Union européenne.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">5. Durée de conservation</h2>
                <p>
                  Vos données de commande sont conservées le temps nécessaire au traitement de la commande
                  puis pour la durée légale de conservation comptable (10 ans en Belgique). Les messages du
                  formulaire de contact sont conservés le temps nécessaire au traitement de votre demande.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">6. Vos droits</h2>
                <p>Conformément au RGPD, vous disposez des droits suivants sur vos données :</p>
                <ul className="mt-3 space-y-1">
                  <li>• Droit d'accès et de rectification</li>
                  <li>• Droit à l'effacement (« droit à l'oubli »)</li>
                  <li>• Droit à la limitation et à l'opposition au traitement</li>
                  <li>• Droit à la portabilité de vos données</li>
                </ul>
                <p className="mt-3">
                  Pour exercer ces droits, écrivez-nous à
                  <a href="mailto:contact@afrionebd.eu" className="text-primary hover:underline"> contact@afrionebd.eu</a>.
                  Vous pouvez également introduire une réclamation auprès de l'Autorité de protection des
                  données (APD) :
                  <a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline"> www.autoriteprotectiondonnees.be</a>.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">7. Cookies et mesure d'audience</h2>

                <h3 className="font-semibold text-text_primary mt-4 mb-2">7.1. Stockage nécessaire au fonctionnement</h3>
                <p>
                  Un stockage technique local (localStorage) mémorise le contenu de votre panier, la langue
                  que vous avez choisie et votre réponse à la bannière de consentement. Il est indispensable
                  au fonctionnement du site, ne permet pas de vous identifier, et n'est transmis à personne.
                  Conformément à l'article 129 de la loi belge du 13 juin 2005, il ne requiert pas votre accord.
                </p>

                <h3 className="font-semibold text-text_primary mt-4 mb-2">7.2. Mesure d'audience sans traceur</h3>
                <p>
                  Nous mesurons la fréquentation du site <strong>sans déposer de cookie et sans conserver
                  votre adresse IP</strong>. Votre navigateur signale la page consultée, notre serveur
                  incrémente un compteur du jour, et rien de plus n'est enregistré : il n'existe aucune
                  fiche individuelle, aucun profil, aucun historique de navigation rattaché à une personne.
                  Seuls des totaux quotidiens existent — nombre de visites par page, produits consultés,
                  paniers constitués.
                </p>
                <p className="mt-2">
                  Nous enregistrons également, sur chaque commande, la façon dont vous êtes arrivé sur le
                  site : moteur de recherche, réseau social, lien partagé, ou accès direct. Cela nous permet
                  de savoir ce qui fait connaître le magasin. La base légale est notre intérêt légitime
                  (article 6.1.f du RGPD) à comprendre et améliorer notre activité ; cette information est
                  conservée avec la commande et n'est jamais transmise à un tiers.
                </p>

                <h3 className="font-semibold text-text_primary mt-4 mb-2">7.3. Mesure affinée (facultative)</h3>
                <p>
                  Avec votre accord, nous retenons votre provenance dans le stockage de session de votre
                  navigateur, le temps de votre visite. Cela évite de compter deux fois votre passage et
                  conserve l'origine de votre commande si vous revenez un peu plus tard. Cette information
                  reste sur votre appareil et ne nous parvient qu'au moment où vous commandez.
                </p>

                <h3 className="font-semibold text-text_primary mt-4 mb-2">7.4. Cookies publicitaires (facultatifs)</h3>
                <p>
                  Si vous l'acceptez, Meta Platforms Ireland Ltd (Facebook, Instagram) et Google Ireland Ltd
                  déposent des cookies permettant de mesurer l'efficacité de nos annonces et de savoir
                  qu'une commande a suivi un clic sur l'une d'elles. Ces données sont transmises à ces
                  sociétés, qui agissent comme responsables de traitement indépendants, et peuvent être
                  transférées aux États-Unis dans le cadre du <em>EU-US Data Privacy Framework</em>.
                </p>
                <p className="mt-2">
                  <strong>Aucun de ces cookies n'est déposé avant votre acceptation</strong> : les scripts
                  correspondants ne sont pas chargés tant que vous n'avez pas répondu, et ne le sont jamais
                  si vous refusez.
                </p>

                <h3 className="font-semibold text-text_primary mt-4 mb-2">7.5. Modifier ou retirer votre choix</h3>
                <p>
                  Le lien <strong>« Gérer mes cookies »</strong>, en bas de chaque page, rouvre la bannière à
                  tout moment. Retirer un accord est aussi simple que de le donner : les cookies publicitaires
                  déjà déposés sont alors supprimés. Refuser n'a aucune conséquence sur votre utilisation du
                  site ni sur vos commandes.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">8. Sécurité</h2>
                <p>
                  Le site est sécurisé par un certificat HTTPS et nous mettons en œuvre des mesures
                  techniques raisonnables pour protéger vos données contre tout accès non autorisé.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
